import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  CircularProgress,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { FaCircleCheck } from "react-icons/fa6";
import SelfServeFields from "./SelfServeFields";
import { FormContext } from "@/providers/form";
import { useContext } from "react";
import { flatMapFields } from "./CategoryProgress";
import { isFieldComplete } from "./CategoryStepper";
import CurrencyInput from "react-currency-input-field";

export const shouldDisplayField = (
  field: any,
  formValues: Record<string, any>
): boolean => {
  if (field.conditionalFn) {
    return field.conditionalFn(formValues);
  }

  return (
    field.displayIf !== false && // Ensure it's not explicitly hidden
    ((!field.displayIf && !field.hideIf) || // Show if neither condition exists
      formValues[field.displayIf] || // Show if displayIf condition is met
      (field.hideIf && formValues[field.hideIf] === false)) // Show if hideIf condition is explicitly false
  );
};

export const calculateSectionProgress = ({
  section,
  values,
}: {
  section: any;
  values: any;
}) => {
  const fields_to_count = section.fields
    .filter((field: any) => shouldDisplayField(field, values))
    .flatMap((field: any) => flatMapFields(field, values))
    .filter((field: any) => shouldDisplayField(field, values));

  const question_count = fields_to_count.length;

  const completed = fields_to_count.filter((field: any) =>
    isFieldComplete(field, values)
  ).length;

  return {
    question_count,
    completed,
    progress: Math.round((completed / question_count) * 100),
  };
};

const SelfServeSection = ({ section }: { section: any }) => {
  const { saveForm, values } = useContext(FormContext);

  const { progress } = calculateSectionProgress({
    section,
    values,
  });

  return (
    <AccordionItem
      key={section.title}
      borderWidth="1px"
      borderColor={progress === 100 ? "brand.primary" : "#E4E4E4"}
      borderRadius="4px"
      mb="32px"
      _last={{
        mb: "0px",
      }}
    >
      {({ isExpanded }) => (
        <>
          <h2>
            <AccordionButton
              py="16px"
              bg={progress === 100 ? "#F5FFF9" : "white"}
              borderRadius="4px"
              _hover={{
                bg: progress === 100 ? "#F5FFF925" : "rgba(0, 0, 0, 0.04)",
              }}
            >
              <Flex alignItems="center" justifyContent="space-between" w="100%">
                <Flex alignItems="center">
                  <Box
                    as="span"
                    flex="1"
                    textAlign="left"
                    fontWeight="500"
                    fontSize="lg"
                    color={section.loaded ? "green" : "black"}
                  >
                    {section.title}
                  </Box>
                </Flex>
                {progress === 100 ? (
                  <Icon
                    as={FaCircleCheck}
                    color="brand.primary"
                    fontSize="24px"
                  />
                ) : (
                  <CircularProgress
                    capIsRound
                    value={progress}
                    size="24px"
                    color="brand.primary"
                  />
                )}
              </Flex>

              <AccordionIcon ml="24px" />
            </AccordionButton>
          </h2>
          {isExpanded && (
            <AccordionPanel pb={4} borderTop="1px solid #E4E4E4">
              {section.fields
                .filter((field: any) => field.displayIf !== false)
                .filter((field: any) => shouldDisplayField(field, values))
                .map((field: any, index: number) =>
                  field.type === "GeneratedList" ? (
                    <Box key={`${field.databaseKey}-${index}`}>
                      {field.renderListFields(values).map((listField: any) => (
                        <FormControl key={listField.label} py="12px">
                          <FormLabel>{listField.label}</FormLabel>
                          <InputGroup
                            key={`${field.databaseKey}-${listField.label}-${index}`}
                          >
                            <InputLeftElement pointerEvents="none">
                              $
                            </InputLeftElement>
                            <Input
                              as={CurrencyInput}
                              w="auto"
                              decimalsLimit={field.decimalsLimit || 2}
                              placeholder={field.placeholder || "Total amount"}
                              defaultValue={
                                (values[field.databaseKey] || {})[
                                  listField.label
                                ]
                              }
                              onBlur={(ev) => {
                                saveForm({
                                  [field.databaseKey]: {
                                    ...(values[field.databaseKey] || {}),
                                    [listField.label]: Number(
                                      (ev.target.value || "").replace(
                                        /[^0-9.-]+/g,
                                        ""
                                      )
                                    ),
                                  },
                                });
                              }}
                            />
                          </InputGroup>
                        </FormControl>
                      ))}
                    </Box>
                  ) : (
                    <SelfServeFields
                      key={`${field.databaseKey}-${index}`}
                      field={field}
                    />
                  )
                )}
            </AccordionPanel>
          )}
        </>
      )}
    </AccordionItem>
  );
};

export default SelfServeSection;
